import React, { useState } from "react";
import { wheel, storiesByTag, targets, reports, fmtDate } from "../../data/impactStories";
import { ArrowUpRight, FileText, Target } from "lucide-react";

const C = 170, R_OUT = 118, R_IN = 66;
const polar = (r, a) => [C + r * Math.cos(a), C + r * Math.sin(a)];

function segPath(a0, a1) {
  const [x0, y0] = polar(R_OUT, a0);
  const [x1, y1] = polar(R_OUT, a1);
  const [x2, y2] = polar(R_IN, a1);
  const [x3, y3] = polar(R_IN, a0);
  const large = a1 - a0 > Math.PI ? 1 : 0;
  return `M${x0},${y0} A${R_OUT},${R_OUT} 0 ${large} 1 ${x1},${y1} L${x2},${y2} A${R_IN},${R_IN} 0 ${large} 0 ${x3},${y3} Z`;
}

export default function RiWheel({ onOpenStory }) {
  const [pillar, setPillar] = useState("environment");
  const [sub, setSub] = useState("carbon");
  const active = wheel.find((w) => w.key === pillar);
  const stories = storiesByTag(sub).slice(0, 3);
  const subTargets = targets.filter((t) => t.tags.includes(sub)).slice(0, 2);
  const subReports = reports.filter((r) => r.tags.includes(sub)).slice(0, 2);
  const subLabel = active.subs.find((s) => s.key === sub)?.label;

  const selectPillar = (w) => {
    setPillar(w.key);
    setSub(w.subs[0].key);
  };

  return (
    <section id="wheel" data-testid="ri-wheel" className="py-24 lg:py-32 bg-[#08080a] scroll-mt-20 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-14 reveal">
          <div className="text-[11px] uppercase tracking-[0.3em] text-amber-accent mb-4">Navigate by Impact</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.02]">
            The wheel is <span className="text-amber-accent">the map.</span>
          </h2>
          <p className="mt-5 text-white/65 leading-relaxed">
            Pick a pillar, pick a topic, the stories, targets and reports behind it appear instantly.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* Wheel */}
          <div className="lg:col-span-5 reveal">
            <div className="relative max-w-[440px] mx-auto aspect-square">
              <svg viewBox="0 0 340 340" className="w-full h-full overflow-visible">
                {wheel.map((w, i) => {
                  const a0 = -Math.PI / 2 + (i * 2 * Math.PI) / 3 + 0.03;
                  const a1 = -Math.PI / 2 + ((i + 1) * 2 * Math.PI) / 3 - 0.03;
                  const mid = (a0 + a1) / 2;
                  const reversed = Math.sin(mid) > 0;
                  const rLab = (R_OUT + R_IN) / 2;
                  const [sx, sy] = polar(rLab, reversed ? a1 : a0);
                  const [ex, ey] = polar(rLab, reversed ? a0 : a1);
                  const labelPath = `M${sx},${sy} A${rLab},${rLab} 0 0 ${reversed ? 0 : 1} ${ex},${ey}`;
                  const isActive = pillar === w.key;
                  return (
                    <g key={w.key} onClick={() => selectPillar(w)} className="cursor-pointer" data-testid={`wheel-seg-${w.key}`}
                      style={{ transformOrigin: "170px 170px", transform: isActive ? "scale(1.04)" : "scale(1)", transition: "transform 0.5s cubic-bezier(0.16,1,0.3,1)" }}>
                      <path d={segPath(a0, a1)} fill={isActive ? w.color : "rgba(255,255,255,0.06)"}
                        stroke={isActive ? w.color : "rgba(255,255,255,0.14)"} strokeWidth="1"
                        style={{ transition: "fill 0.4s ease" }} />
                      <path id={`wheel-lbl-${w.key}`} d={labelPath} fill="none" />
                      <text fontSize="14" fontWeight="700" fill={isActive ? "#0a0a0a" : "rgba(255,255,255,0.75)"}
                        className="font-display pointer-events-none" style={{ transition: "fill 0.4s ease" }}>
                        <textPath href={`#wheel-lbl-${w.key}`} startOffset="50%" textAnchor="middle" dominantBaseline="central">
                          {w.label}
                        </textPath>
                      </text>
                    </g>
                  );
                })}
                <circle cx={C} cy={C} r={R_IN - 10} fill="#101014" stroke="rgba(255,255,255,0.1)" />
                <text x={C} y={C - 8} textAnchor="middle" fontSize="11" fill="rgba(255,255,255,0.5)" letterSpacing="2">RESPONSIBLE</text>
                <text x={C} y={C + 10} textAnchor="middle" fontSize="11" fill="#F5D000" letterSpacing="2">INFRASTRUCTURE</text>
              </svg>
            </div>
            {/* Sub topics */}
            <div className="mt-6 flex flex-wrap justify-center gap-2" data-testid="wheel-subs">
              {active.subs.map((s) => (
                <button
                  key={s.key}
                  data-testid={`wheel-sub-${s.key}`}
                  onClick={() => setSub(s.key)}
                  className={`px-4 py-2 rounded-full text-xs uppercase tracking-[0.16em] font-semibold border transition-all duration-300 ${
                    sub === s.key ? "bg-[#F5D000] text-black border-[#F5D000]" : "text-white/60 border-white/15 hover:border-white/40 hover:text-white"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Panel */}
          <div className="lg:col-span-7" data-testid="wheel-panel" key={`${pillar}-${sub}`}>
            <div className="ri-fade-up">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#F5D000] mb-1">{active.label} · {subLabel}</div>
              <h3 className="font-display text-2xl font-bold text-white mb-6">Everything we hold on {subLabel?.toLowerCase()}.</h3>

              {stories.length > 0 ? (
                <div className="space-y-3 mb-7">
                  {stories.map((s) => (
                    <button key={s.id} data-testid={`wheel-story-${s.id}`} onClick={() => onOpenStory(s)}
                      className="group w-full text-left rounded-xl bg-[#101014] border border-white/8 hover:border-[#F5D000]/50 transition-all p-4 flex items-center gap-4">
                      <div className="w-16 h-14 shrink-0 rounded-lg overflow-hidden">
                        <img src={s.image} alt="" className="w-full h-full object-cover" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[10px] text-white/45 mb-0.5">{fmtDate(s.date)}</div>
                        <div className="font-display text-base font-bold text-white leading-snug truncate">{s.title}</div>
                      </div>
                      <ArrowUpRight className="w-4 h-4 text-white/30 group-hover:text-[#F5D000] transition-colors shrink-0" />
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-white/50 text-sm mb-7">Impact Stories for this topic will appear here as they're recorded.</p>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {subTargets.map((t) => (
                  <div key={t.id} className="rounded-xl border border-white/8 bg-white/[0.03] p-4">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#F5D000] mb-1.5">
                      <Target className="w-3.5 h-3.5" /> Target · {t.status}
                    </div>
                    <div className="text-sm font-semibold text-white">{t.title}</div>
                  </div>
                ))}
                {subReports.map((r) => (
                  <div key={r.id} className="rounded-xl border border-white/8 bg-white/[0.03] p-4">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-white/50 mb-1.5">
                      <FileText className="w-3.5 h-3.5 text-[#F5D000]" /> {r.category} · {r.version}
                    </div>
                    <div className="text-sm font-semibold text-white">{r.title}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
